import { useEffect, useId, useRef } from 'react';
import * as modalStack from './modalStack';
import { initPopstateDispatcher } from './popstateDispatcher';

export interface UseModalBackDismissOptions {
  isOpen: boolean;
  onClose: () => void;
  canDismiss?: () => boolean;
}

// Keyed by modal id. A cleanup defers its actual work (see scheduleCleanup
// below) instead of running it inline, so a same-tick remount — React
// StrictMode's dev-only double-invoke of every effect at mount — can cancel
// it before it does anything, rather than leaving behind a real but
// content-duplicate history entry that a genuine close/reopen never would
// have produced. Each such duplicate is invisible (its content matches
// whatever's already on screen) but still consumes one real back press,
// which compounds badly: a StrictMode-doubled open followed by a
// back-triggered close can otherwise leave 2-3 duplicates in a row before
// the next visibly-different entry, reading as "back does nothing" several
// times over.
const pendingCleanup = new Map<string, { cancelled: boolean }>();

/**
 * Wires a modal into the shared back-stack so a back press dismisses it
 * before any tab-level navigation occurs. `onClose` should be the same
 * close-handler the modal's own Cancel/X button uses (e.g. `handleCancel`),
 * including any confirm-before-close or edit->view-revert behavior it has —
 * this hook tolerates a close-handler that doesn't always actually close.
 */
export function useModalBackDismiss({
  isOpen,
  onClose,
  canDismiss = () => true,
}: UseModalBackDismissOptions): void {
  const id = useId();
  const onCloseRef = useRef(onClose);
  const canDismissRef = useRef(canDismiss);
  const previousStateRef = useRef<unknown>(undefined);

  onCloseRef.current = onClose;
  canDismissRef.current = canDismiss;

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    function scheduleCleanup(): void {
      const record = { cancelled: false };
      pendingCleanup.set(id, record);
      queueMicrotask(() => {
        if (pendingCleanup.get(id) !== record) {
          return;
        }
        pendingCleanup.delete(id);
        if (record.cancelled) {
          return;
        }
        modalStack.remove(id);
        // Closed via a non-back trigger (Save/Cancel/X/Escape/backdrop), or
        // this is the real close following a back-triggered decline/revert
        // that re-pushed below — either way, replace whatever is currently
        // the top entry with what preceded this modal, rather than
        // navigating back. `history.back()`'s target is fixed at call time,
        // not when it resolves — under React StrictMode's synchronous
        // cleanup->remount cycle, a remount's own pushState can run before
        // a queued back() resolves, so by the time it fires it overshoots
        // past the freshly pushed entry. `replaceState` is synchronous and
        // never fires `popstate`, so it sidesteps that race entirely.
        window.history.replaceState(previousStateRef.current, '');
      });
    }

    const inherited = pendingCleanup.get(id);
    if (inherited) {
      // This mount runs synchronously right after this same id's own
      // cleanup deferred itself above — the StrictMode double-invoke, not a
      // genuine close-then-reopen. Cancel the deferred work and continue
      // treating the original mount's entry as still valid.
      inherited.cancelled = true;
      pendingCleanup.delete(id);
      return scheduleCleanup;
    }

    function pushEntry(): void {
      modalStack.push({
        id,
        canDismiss: () => canDismissRef.current(),
        onClose: () => {
          // Re-push immediately, before calling the real close-handler,
          // rather than waiting to see if a later render reveals the modal
          // is still open. `onClose` (e.g. `handleCancel`) may decline to
          // close without triggering any state change at all — e.g. the
          // user cancels a "discard unsaved changes?" confirm — so no
          // render (and no chance to notice that) would otherwise follow.
          // If `onClose` does fully close the modal, this effect's own
          // cleanup collapses the optimistic entry straight back down, so
          // there's no dangling extra entry either way.
          pushEntry();
          onCloseRef.current();
        },
      });
      window.history.pushState({ navType: 'modal', modalId: id }, '');
    }

    previousStateRef.current = window.history.state;
    initPopstateDispatcher();
    pushEntry();

    return scheduleCleanup;
  }, [isOpen, id]);
}
